import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import {
  correctionMethodType,
  type default as CorrectionMethodType,
} from './CorrectionMethodType.js'
import { legacySiteId, type LegacySiteId } from './LegacySite.js'
import {
  sensorMeasurementKindId,
  type SensorMeasurementKindId,
} from './SensorMeasurementKind.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.legacy_site_correction_history_ */
export type LegacySiteCorrectionHistoryId = string & {
  __brand: 'LegacySiteCorrectionHistoryId'
}

/** Represents the table public.legacy_site_correction_history_ */
export default interface LegacySiteCorrectionHistoryTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<
    LegacySiteCorrectionHistoryId,
    LegacySiteCorrectionHistoryId | undefined,
    LegacySiteCorrectionHistoryId
  >

  /** Database type: pg_catalog.int4 */
  siteId: ColumnType<LegacySiteId, LegacySiteId, LegacySiteId>

  /** Database type: pg_catalog.timestamptz */
  startedAt: ColumnType<Date, Date | string, Date | string>

  /** Database type: pg_catalog.timestamptz */
  endedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.text */
  sensorMeasurementKind: ColumnType<
    SensorMeasurementKindId,
    SensorMeasurementKindId,
    SensorMeasurementKindId
  >

  /** Database type: pg_catalog.float8 */
  correctionValue: ColumnType<number, number, number>

  /** Database type: public.correction_method_type_ */
  correctionMethod: ColumnType<
    CorrectionMethodType,
    CorrectionMethodType | undefined,
    CorrectionMethodType
  >

  /** Database type: pg_catalog.text */
  notes: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>
}

export type LegacySiteCorrectionHistory =
  Selectable<LegacySiteCorrectionHistoryTable>

export type LegacySiteCorrectionHistoryInitializer =
  Insertable<LegacySiteCorrectionHistoryTable>

export type LegacySiteCorrectionHistoryMutator =
  Updateable<LegacySiteCorrectionHistoryTable>

export const legacySiteCorrectionHistoryId =
  z.string() as unknown as z.Schema<LegacySiteCorrectionHistoryId>

export const legacySiteCorrectionHistory = z.object({
  id: legacySiteCorrectionHistoryId,
  siteId: legacySiteId,
  startedAt: z.coerce.date(),
  endedAt: z.coerce.date().nullable().optional(),
  sensorMeasurementKind: sensorMeasurementKindId,
  correctionValue: z.number(),
  correctionMethod: correctionMethodType,
  notes: z.string().nullable().optional(),
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
}) as unknown as z.Schema<LegacySiteCorrectionHistory>

export const legacySiteCorrectionHistoryInitializer = z.object({
  id: legacySiteCorrectionHistoryId.optional(),
  siteId: legacySiteId,
  startedAt: z.coerce.date(),
  endedAt: z.coerce.date().optional().nullable(),
  sensorMeasurementKind: sensorMeasurementKindId,
  correctionValue: z.number(),
  correctionMethod: correctionMethodType.optional(),
  notes: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
}) as unknown as z.Schema<LegacySiteCorrectionHistoryInitializer>

export const legacySiteCorrectionHistoryMutator = z.object({
  id: legacySiteCorrectionHistoryId.optional(),
  siteId: legacySiteId.optional(),
  startedAt: z.coerce.date().optional(),
  endedAt: z.coerce.date().optional().nullable(),
  sensorMeasurementKind: sensorMeasurementKindId.optional(),
  correctionValue: z.number().optional(),
  correctionMethod: correctionMethodType.optional(),
  notes: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
}) as unknown as z.Schema<LegacySiteCorrectionHistoryMutator>
